import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import { useDrawMapStore, useMapStore } from "../..";
import useEffectWhenStyleIsLoaded from "../../hooks/useEffectWhenStyleIsLoaded";
import { DrawMapMode } from "../../store/DrawMapStore";

export const readonlyDrawMapLayerId = "readonlyDrawMapLayer";
const readonlyAvailableModes = [DrawMapMode.FocusArea, DrawMapMode.Insights, DrawMapMode.MeasureArea, DrawMapMode.MeasureDistance];

const DrawMapControl = () => {
    const mapStore = useMapStore();
    const drawMapStore = useDrawMapStore();
    const map = useMap().current!;
    const draw = useMemo(() => new MapboxDraw({
        displayControlsDefault: false,
        styles: mapboxDrawStyles,
    }), []);

    useEffectWhenStyleIsLoaded(() => {
        map.addControl(draw);
        map.on("draw.create", () => {
            updateArea();
            if (readonlyAvailableModes.includes(drawMapStore.mode))
                drawMapStore.setIsReadonly(true);
        });

        map.on("draw.delete", updateArea);
        map.on("draw.update", updateArea);
        map.on("draw.modechange", (e) => {
            if (e.mode === "simple_select" && readonlyAvailableModes.includes(drawMapStore.mode))
                drawMapStore.setIsReadonly(true);

            if (!drawMapStore.result)
                drawMapStore.clear();
            if (drawMapStore.mode === DrawMapMode.OneMoreDealShape)
                drawMapStore.setDealModeBack();
        });

        function updateArea() {
            const data = draw.getAll();
            if (data.features.length)
                drawMapStore.setResult(data);
        }
    }, []);

    useEffectWhenStyleIsLoaded(() => {
        const unsubscribeMouseClick = mapStore.onMouseClick("draw", feature => {
            if (feature?.layer?.id === readonlyDrawMapLayerId)
                drawMapStore.setIsReadonly(false);
        });

        return unsubscribeMouseClick;
    }, []);

    useEffectWhenStyleIsLoaded(() => {
        if ([DrawMapMode.Deal, DrawMapMode.TenantInMarket].includes(drawMapStore.mode))
            return;

        if (drawMapStore.mode === DrawMapMode.OneMoreDealShape) {
            draw.changeMode("draw_polygon");
            return;
        }

        draw.deleteAll();
        if (drawMapStore.mode === DrawMapMode.None)
            draw.changeMode("simple_select");

        if (!drawMapStore.result && [DrawMapMode.MeasureArea, DrawMapMode.FocusArea, DrawMapMode.Insights, DrawMapMode.NewTenantInMarketShape].includes(drawMapStore.mode))
            draw.changeMode("draw_polygon");

        if (drawMapStore.mode === DrawMapMode.MeasureDistance)
            draw.changeMode("draw_line_string");
    }, [drawMapStore.mode]);

    useEffectWhenStyleIsLoaded(() => {
        if (drawMapStore.result && !drawMapStore.isReadonly)
            draw.set(drawMapStore.result);

        if (!drawMapStore.result && drawMapStore.mode === DrawMapMode.MeasureArea) {
            draw.deleteAll();
            draw.changeMode("draw_polygon");
        }

        if (!drawMapStore.result && drawMapStore.mode === DrawMapMode.MeasureDistance) {
            draw.deleteAll();
            draw.changeMode("draw_line_string");
        }
    }, [drawMapStore.result]);

    useEffectWhenStyleIsLoaded(() => {
        if (!readonlyAvailableModes.includes(drawMapStore.mode))
            return;

        if (drawMapStore.isReadonly) {
            draw.deleteAll();
            draw.changeMode("simple_select");
        } else if (drawMapStore.result) {
            draw.set(drawMapStore.result);
            draw.changeMode("direct_select", { featureId: drawMapStore.result.features[0]!.id as string });
        }
    }, [drawMapStore.isReadonly]);

    return drawMapStore.isReadonly ? (
        <Source id="readonlySource" type="geojson" data={drawMapStore.result}>
            <Layer
                id={readonlyDrawMapLayerId}
                type="line"
                layout={mapboxDrawStyles.find(s => s.id === "gl-draw-polygon-stroke-active")!.layout as any}
                paint={mapboxDrawStyles.find(s => s.id === "gl-draw-polygon-stroke-active")!.paint}
            />
        </Source>
    ) : null;
};

export default observer(DrawMapControl);

const mapboxDrawStyles = [
    {
        id: "gl-draw-polygon-fill",
        type: "fill",
        filter: ["==", "$type", "Polygon"],
        paint: {
            "fill-color": "#F1971F",
            "fill-opacity": 0,
        },
    },
    {
        id: "gl-draw-polygon-stroke-active",
        type: "line",
        filter: [
            "any",
            ["==", "$type", "Polygon"],
            [
                "all",
                ["==", "$type", "LineString"],
                ["==", "active", "true"],
            ],
        ],
        layout: {
            "line-cap": "butt",
            "line-join": "bevel",
        },
        paint: {
            "line-color": "#F1971F",
            "line-dasharray": [1, 2],
            "line-width": 5,
        },
    },
    {
        id: "gl-draw-polygon-stroke-inactive",
        type: "line",
        filter: [
            "all",
            ["==", "$type", "LineString"],
            ["==", "active", "false"],
        ],
        layout: {
            "line-cap": "butt",
            "line-join": "bevel",
        },
        paint: {
            "line-color": "#F1971F",
            "line-dasharray": [1, 2],
            "line-width": 5,
        },
    },

    {
        id: "gl-draw-polygon-midpoint",
        type: "circle",
        filter: [
            "all",
            ["==", "$type", "Point"],
            ["==", "meta", "midpoint"],
        ],
        paint: {
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
            "circle-radius": 3,
            "circle-color": "#F1971F",
        },
    },
    {
        id: "gl-draw-point-default",
        type: "circle",
        filter: [
            "all",
            ["==", "active", "false"],
            ["==", "meta", "vertex"],
            ["==", "$type", "Point"],
        ],
        paint: {
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
            "circle-radius": 5,
            "circle-color": "#F1971F",
        },
    },
    {
        id: "gl-draw-point-active",
        type: "circle",
        filter: [
            "all",
            ["==", "$type", "Point"],
            ["!=", "meta", "midpoint"],
            ["==", "active", "true"],
        ],
        paint: {
            "circle-stroke-width": 3,
            "circle-stroke-color": "#ffffff",
            "circle-radius": 5,
            "circle-color": "#F1971F",
        },
    },
];